<template>
	<div>
		<v-data-table
			v-if="mappedOrganizations"
			dense
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')"
			:loading="loading"
			:items="groupedOrganizations"
			item-key="name"
			single-expand
			:height="pageHeight(174)"
			hide-default-header
			ref="table"
			:items-per-page="-1"
			:footer-props="getDataTableFooterProps('officesTable')">
			<template #item="{ item, index, expand, isExpanded }">
				<tr :class="[getRowsForDataTable(index), { 'expanded-row': isExpanded }]" @click="expand(!isExpanded)">
					<td class="text-left">
						<v-icon small color="primary" class="mr-1">
							{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
						</v-icon>
						{{ item.name }}
						<v-chip pill x-small color="primary" class="mx-2" label>
							{{ item.organizations.length }}
							{{ item.organizations.length > 1 ? $t('organizations') : $t('organization') }}
						</v-chip>
					</td>
				</tr>
			</template>
			<template #expanded-item="{ item }">
				<tr style="cursor: default; background-color: #fff !important">
					<td class="pa-3">
						<v-data-table
							:items="item.organizations"
							:headers="insideHeaders"
							dense
							:items-per-page="-1"
							hide-default-footer
							class="data-table-outline"
							:height="pageHeight(84)"
							fixed-header>
							<template #item="{ item, index }">
								<tr @click="editOrganizationMapping(item)" :class="[getRowsForDataTable(index)]" class="text-left">
									<td>{{ item.ecpCompanyName }}</td>
									<td>{{ item.ecpCompanyId }}</td>
									<td class="text-right">
										<v-menu left bottom class="py-0" offset-y transition="slide-x-transition">
											<template #activator="{ on, attrs }">
												<v-btn icon color="main_black" v-bind="attrs" v-on="on">
													<v-icon>mdi-dots-horizontal</v-icon>
												</v-btn>
											</template>
											<v-list class="px-0" dense>
												<v-list-item class="py-0 my-0" link dense @click="editOrganizationMapping(item)">
													<v-list-item-icon class="mx-1">
														<v-icon class="icon-img mdi-18px" color="black">mdi-eye</v-icon>
													</v-list-item-icon>
													<v-list-item-content class="font-bold font-size14">
														{{ $t('view') }}
													</v-list-item-content>
												</v-list-item>
												<v-divider />
												<v-list-item link dense @click="openDialog(item)">
													<v-list-item-icon class="mx-1">
														<v-icon v-if="roleName === 'SYSTEM_ADMIN'" color="main_red">mdi-trash-can-outline</v-icon>
													</v-list-item-icon>
													<v-list-item-content class="font-bold font-size14">
														{{ $t('delete') }}
													</v-list-item-content>
												</v-list-item>
											</v-list>
										</v-menu>
									</td>
								</tr>
							</template>
						</v-data-table>
					</td>
				</tr>
			</template>
		</v-data-table>
		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deleteHospitalConfirm();
			" />
	</div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { ecp, patientBlack, logoDrawer } from '@/assets';
import MappingRepository from '@/data/repositories/v1/MappingRepository.js';
import { getDataTableFooterProps, getRowsForDataTable } from '@/utils/utilities';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';

export default {
	data () {
		return {
			dialog: false,
			showDeleteDialog: false,
			resident: null,
			patientToDelete: null,
			PatientBlack: patientBlack,
			loading: false,
			officeId: null,
			selectedEcpUser: null,
			selectedNotMappedUser: null,
			searchEcpPatient: '',
			searchNotMappedPatient: '',
			searchMappedPatient: '',
			mappedOrganizations: [],
			items: {
				notMappedUsers: [],
				mappedUsers: [],
				usersNeedToBeMapped: [],
			},
		};
	},
	components: { DeleteDialog },
	computed: {
		logoDrawer () {
			return logoDrawer;
		},
		ecp () {
			return ecp;
		},
		...mapState({
			userData: (state) => state.authentication.userData,
			selectedOrganizationId: (state) => state.filterbar.hospitalId,
			medicalInstitution: (state) => state.filterbar.medicalInstitution,
		}),
		...mapGetters({
			roleName: 'authentication/getRole',
			userSettings: 'authentication/getUserSettings',
			pageHeight: 'pageHeight',
			appBarSearch: 'filterbar/getSearch',
		}),
		// map state if needed
		detailsDrawer () {
			return this.$store.state.detailsDrawer;
		},
		groupedOrganizations () {
			const search = this.appBarSearch?.toLowerCase();
			return this.mappedOrganizations.reduce((groups, org) => {
				if (search) {
					const name = org.medicalInstitution.institutionName.toLowerCase();
					const ecpCompanyName = org.ecpCompanyName.toLowerCase();
					const ecpCompanyId = org.ecpCompanyId.toLowerCase();
					if (!name.includes(search) && !ecpCompanyName.includes(search) && !ecpCompanyId.includes(search)) {
						return groups;
					}
				}
				let group = groups.find((g) => g.name === org.medicalInstitution.institutionName);
				if (!group) {
					group = { name: org.medicalInstitution.institutionName, organizations: [] };
					groups.push(group);
				}
				group.organizations.push(org);
				return groups;
			}, []);
		},
		insideHeaders () {
			return [
				{ text: this.$t('company-name'), value: 'ecpCompanyName' },
				{ text: 'GUID', value: 'ecpCompanyId' },
				{ text: '', value: 'actions' },
			];
		},
	},
	watch: {
		showDeleteDialog (val) {
			if (val === false) {
				// means when the delete dialog is being closed
				this.getMappedEcpOrganization();
			}
		},
		detailsDrawer (val) {
			if (val === false) {
				// means when the delete dialog is being closed
				this.getMappedEcpOrganization();
			}
		},
	},
	methods: {
		openDialog (item) {
			this.showDeleteDialog = true;
			this.officeId = item.id;
		},
		...mapActions({}),
		getRowsForDataTable,
		getDataTableFooterProps,
		openDeleteDialog (item) {
			this.showDeleteDialog = true;
			this.patientToDelete = item.id;
		},
		getUsersForMapping () {
			this.loading = true;

			let selectedOrganizationId = null;
			if (this.roleName === 'HOSPITAL_ADMIN') {
				selectedOrganizationId = this.userData.hospitalAssignedData.id;
			} else {
				selectedOrganizationId = this.selectedOrganizationId;
			}

			MappingRepository.getMappedEcpUsersForOrganization(selectedOrganizationId).then((response) => {
				this.items = response || [];
				this.loading = false;
			});
		},
		async editOrganizationMapping (item) {
			await this.$store.commit('mapping/SELECTED_ORGANIZATION', item);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', true);
		},
		getMappedEcpOrganization () {
			this.loading = true;
			MappingRepository.getMappedEcpOrganization().then((res) => {
				this.mappedOrganizations = res;
				this.loading = false;
			});
		},
		async deleteHospitalConfirm () {
			await MappingRepository.deleteMappedEcpOrganization(this.officeId).then(() => this.getMappedEcpOrganization());
		},
	},
	async mounted () {
		this.getMappedEcpOrganization();
	},
};
</script>

<style>
.v-messages {
	display: none !important;
}

.small i {
	font-size: 18px !important;
}

.small [class*='__ripple'] {
	left: 0;
	display: none;
}
</style>
